import { Flex, Grid, ReadonlyField, Text, WatchLaterOutlinedIcon } from '@shared/components';
import { dayjs, formatTimeToThaiUnits } from '@shared/utils';

import { type DisplaySession } from '../../hooks';

export interface ExamTimeReadonlyFieldsProps {
  examDate: {
    examStartedAt: Date;
    examEndedAt: Date;
  };
  sessions: {
    data: DisplaySession[];
    totalDuration: {
      hours: number;
      minutes: number;
    };
  };
}

export default function ExamTimeReadonlyFields(props: Readonly<ExamTimeReadonlyFieldsProps>) {
  const { examDate, sessions } = props;

  const sessionDataOrderByRound = sessions.data.sort((a, b) => a.order - b.order);

  const shouldDisplayTotalDuration = sessionDataOrderByRound.length > 1;
  return (
    <Grid
      sx={{
        columnGap: (theme) => theme.spacing(2),
        rowGap: (theme) => theme.spacing(2),
        gridTemplateColumns:
          'minmax(max-content, 200px) max-content minmax(max-content, 200px) auto',
        width: '100%',
      }}
    >
      <ReadonlyField
        label='เวลาเริ่มต้น'
        content={
          <Flex
            sx={{
              gap: (theme) => theme.spacing(1),
              alignItems: 'center',
              justifyContent: 'space-between',
            }}
          >
            <Text>{dayjs(examDate.examStartedAt).format('HH:mm')}</Text>
            <WatchLaterOutlinedIcon
              sx={(theme) => ({
                color: theme.palette.text.secondary,
              })}
            />
          </Flex>
        }
      />
      <Text sx={{ p: (theme) => theme.spacing(8.5, 1, 2.5, 1) }}>ถึง</Text>
      <ReadonlyField
        label='เวลาสิ้นสุด'
        content={
          <Flex
            sx={{
              gap: (theme) => theme.spacing(1),
              alignItems: 'center',
              justifyContent: 'space-between',
            }}
          >
            <Text>{dayjs(examDate.examEndedAt).format('HH:mm')}</Text>
            <WatchLaterOutlinedIcon
              sx={(theme) => ({
                color: theme.palette.text.secondary,
              })}
            />
          </Flex>
        }
      />
      {sessionDataOrderByRound.map((session) => (
        <Flex
          key={session.order}
          sx={(theme) => ({
            padding: theme.spacing(4),
            gap: theme.spacing(6),
            borderRadius: theme.shape.rounded(1),
            bgcolor: theme.palette.grey[100],
            gridColumn: '1 / -1',
          })}
        >
          <Text variant='body1Semibold'>ช่วงที่ {session.order}</Text>
          <Flex
            sx={{
              gap: (theme) => theme.spacing(4),
            }}
          >
            <Text>
              {dayjs(session.startedAt).format('HH:mm')} - {dayjs(session.endedAt).format('HH:mm')}
            </Text>
            <Text>ระยะเวลา: {formatTimeToThaiUnits(session.duration)}</Text>
          </Flex>
        </Flex>
      ))}
      {shouldDisplayTotalDuration ? (
        <Flex
          sx={(theme) => ({
            alignItems: 'center',
            gap: theme.spacing(2),
            color: theme.palette.text.primary,
            padding: theme.spacing(3, 4),
            borderRadius: theme.shape.rounded(2),
            bgcolor: theme.palette.grey[100],
            gridColumn: '1 / -1',
          })}
        >
          <WatchLaterOutlinedIcon />
          <Text variant='body1Semibold'>
            รวมระยะเวลาสอบ: {formatTimeToThaiUnits(sessions.totalDuration)}
          </Text>
        </Flex>
      ) : null}
    </Grid>
  );
}
